// src/pages/auth/LoginPage.js
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import { login as apiLogin } from '../../services/api';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const Icon = ({ d, size = 18, strokeWidth = 1.6 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    {d}
  </svg>
);

const icons = {
  user: <><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  lock: <><rect x="4" y="11" width="16" height="10" rx="2" /><path d="M8 11V7a4 4 0 0 1 8 0v4" /></>,
  eye: <><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></>,
  eyeOff: <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 19c-6.5 0-10-7-10-7a18.4 18.4 0 0 1 5.06-5.94M9.9 5.24A9.1 9.1 0 0 1 12 5c6.5 0 10 7 10 7a18.5 18.5 0 0 1-2.16 3.19M14.12 14.12a3 3 0 1 1-4.24-4.24" /><path d="M2 2l20 20" /></>,
  chart: <><path d="M3 3v18h18" /><path d="M7 15l4-4 3 3 5-6" /></>,
  users: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  file: <><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" /><path d="M14 2v6h6" /><path d="M8 13h8M8 17h5" /></>,
  arrow: <><path d="M5 12h14" /><path d="M13 6l6 6-6 6" /></>,
  cap: <><path d="M22 10L12 5 2 10l10 5 10-5z" /><path d="M6 12v5c0 1.1 2.7 3 6 3s6-1.9 6-3v-5" /></>,
};

const features = [
  { icon: icons.chart, title: 'คะแนนและตัวชี้วัด', desc: 'บันทึกคะแนนตามหน่วยการเรียนรู้ ครบทุกตัวชี้วัด' },
  { icon: icons.users, title: 'ครูและนักเรียน', desc: 'จัดการห้องเรียน เวลาเรียน และกิจกรรมพัฒนาผู้เรียน' },
  { icon: icons.file, title: 'เอกสารและรายงาน', desc: 'ออกรายงาน ปพ.5 ปพ.6 และสรุปผลได้ทันที' },
];

export default function LoginPage() {
  const [form, setForm] = useState({ username: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [logoOk, setLogoOk] = useState(true);
  const { login } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.username || !form.password) {
      toast.warning('กรุณากรอกชื่อผู้ใช้และรหัสผ่าน');
      return;
    }
    setLoading(true);
    try {
      const res = await apiLogin(form);
      if (res.data.success) {
        login(res.data.token, res.data.user);
        sessionStorage.setItem('wadphol_show_login_popup', '1');
        toast.success('ยินดีต้อนรับ ' + res.data.user.firstname + ' ' + res.data.user.lastname);
        const returnTo = sessionStorage.getItem('wadphol_return_to');
        sessionStorage.removeItem('wadphol_return_to');
        navigate(returnTo && returnTo !== '/login' ? returnTo : '/');
      } else {
        toast.error(res.data.message);
      }
    } catch (err) {
      const isTimeout = err?.code === 'ECONNABORTED' || err?.code === 'ETIMEDOUT';
      const msg = err.response?.data?.message
        || (isTimeout ? 'เซิร์ฟเวอร์ไม่ตอบสนอง กรุณาลองใหม่ หรือติดต่อผู้ดูแลระบบ' : null)
        || (err.response ? 'เข้าสู่ระบบไม่สำเร็จ' : 'ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้');
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const year = new Date().getFullYear() + 543;

  return (
    <div className="lp-root">
      <style>{css}</style>

      <aside className="lp-side">
        <div className="lp-side-inner">
          <div className="lp-brand">
            <div className="lp-logo">
              {logoOk
                ? <img src={`${API_BASE}/school/logo.php`} alt="" onError={() => setLogoOk(false)} />
                : <Icon d={icons.cap} size={26} />}
            </div>
            <div>
              <div className="lp-brand-name">ระบบวัดผลการศึกษา</div>
              <div className="lp-brand-sub">Academic Assessment System</div>
            </div>
          </div>

          <div className="lp-hero">
            <h1>ติดตามผลการเรียน<br />ได้ในที่เดียว</h1>
            <p>บริหารจัดการคะแนน ตัวชี้วัด เวลาเรียน และเอกสารทางการศึกษาของโรงเรียน</p>
          </div>

          <ul className="lp-features">
            {features.map(f => (
              <li key={f.title}>
                <span className="lp-feat-icon"><Icon d={f.icon} size={18} /></span>
                <div>
                  <div className="lp-feat-title">{f.title}</div>
                  <div className="lp-feat-desc">{f.desc}</div>
                </div>
              </li>
            ))}
          </ul>

          <div className="lp-side-foot">© {year} · พัฒนาโดย นันทวิทย์ ชูเชื้อ</div>
        </div>
      </aside>

      <main className="lp-main">
        <div className="lp-card">
          <div className="lp-mobile-brand">
            <div className="lp-logo lp-logo-sm">
              {logoOk
                ? <img src={`${API_BASE}/school/logo.php`} alt="" onError={() => setLogoOk(false)} />
                : <Icon d={icons.cap} size={22} />}
            </div>
            <span>ระบบวัดผลการศึกษา</span>
          </div>

          <h2>เข้าสู่ระบบ</h2>
          <p className="lp-lead">ใช้ชื่อผู้ใช้และรหัสผ่านที่ได้รับจากโรงเรียน</p>

          <form onSubmit={handleSubmit} noValidate>
            <label className="lp-label" htmlFor="lp-username">ชื่อผู้ใช้งาน</label>
            <div className="lp-field">
              <span className="lp-field-icon"><Icon d={icons.user} size={17} /></span>
              <input id="lp-username" type="text" autoComplete="username" autoFocus
                placeholder="ชื่อผู้ใช้" value={form.username}
                onChange={e => setForm({ ...form, username: e.target.value })} />
            </div>

            <label className="lp-label" htmlFor="lp-password">รหัสผ่าน</label>
            <div className="lp-field">
              <span className="lp-field-icon"><Icon d={icons.lock} size={17} /></span>
              <input id="lp-password" type={showPassword ? 'text' : 'password'} autoComplete="current-password"
                placeholder="รหัสผ่าน" value={form.password}
                onChange={e => setForm({ ...form, password: e.target.value })} />
              <button type="button" className="lp-eye" onClick={() => setShowPassword(v => !v)}
                aria-label={showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}>
                <Icon d={showPassword ? icons.eyeOff : icons.eye} size={18} />
              </button>
            </div>

            <button type="submit" className="lp-submit" disabled={loading}>
              {loading ? (
                <><span className="lp-spinner" />กำลังเข้าสู่ระบบ</>
              ) : (
                <>เข้าสู่ระบบ<Icon d={icons.arrow} size={18} strokeWidth={2} /></>
              )}
            </button>
          </form>
          <Link className="lp-student-entry" to="/student-results">
            <span className="lp-student-icon"><Icon d={icons.chart} size={24} strokeWidth={1.8} /></span>
            <span className="lp-student-copy"><strong>สำหรับนักเรียน</strong><span>เข้าสู่ระบบดูผลการเรียนออนไลน์</span></span>
            <Icon d={icons.arrow} size={20} />
          </Link>

          <p className="lp-help">ลืมรหัสผ่านหรือเข้าระบบไม่ได้ กรุณาติดต่อฝ่ายวัดผลหรือผู้ดูแลระบบ</p>
        </div>

        <div className="lp-mobile-foot">© {year} · พัฒนาโดย นันทวิทย์ ชูเชื้อ</div>
      </main>
    </div>
  );
}

const css = `
.lp-root {
  --ink: #0f172a; --muted: #64748b; --line: #e2e8f0;
  --brand: #12355b; --brand-2: #1d4f86; --accent: #c9a24a;
  min-height: 100vh; display: flex; font-family: 'Sarabun', sans-serif; color: var(--ink); background: #fff;
}
.lp-side {
  flex: 1 1 52%; background: var(--brand); color: #fff; position: relative; overflow: hidden;
  display: flex; padding: 48px 56px;
}
.lp-side::before {
  content: ''; position: absolute; inset: 0; pointer-events: none;
  background-image:
    linear-gradient(rgba(255,255,255,.045) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255,255,255,.045) 1px, transparent 1px);
  background-size: 44px 44px;
  mask-image: linear-gradient(160deg, #000 10%, transparent 75%);
  -webkit-mask-image: linear-gradient(160deg, #000 10%, transparent 75%);
}
.lp-side::after {
  content: ''; position: absolute; left: 56px; bottom: 0; width: 64px; height: 4px; background: var(--accent);
}
.lp-side-inner { position: relative; display: flex; flex-direction: column; width: 100%; max-width: 520px; }
.lp-brand { display: flex; align-items: center; gap: 14px; }
.lp-logo {
  width: 52px; height: 52px; border-radius: 12px; background: #fff; color: var(--brand);
  display: flex; align-items: center; justify-content: center; overflow: hidden; flex-shrink: 0;
}
.lp-logo img { width: 100%; height: 100%; object-fit: contain; padding: 4px; }
.lp-logo-sm { width: 40px; height: 40px; border-radius: 10px; border: 1px solid var(--line); }
.lp-brand-name { font-size: 17px; font-weight: 700; letter-spacing: .1px; }
.lp-brand-sub { font-size: 12.5px; color: rgba(255,255,255,.6); letter-spacing: .4px; }

.lp-hero { margin: auto 0 40px; padding-top: 64px; }
.lp-hero h1 { font-size: 40px; line-height: 1.25; font-weight: 700; margin: 0 0 16px; letter-spacing: -.2px; }
.lp-hero p { font-size: 16px; line-height: 1.7; color: rgba(255,255,255,.72); margin: 0; max-width: 420px; }

.lp-features { list-style: none; margin: 0 0 auto; padding: 0; border-top: 1px solid rgba(255,255,255,.14); }
.lp-features li {
  display: flex; gap: 16px; align-items: flex-start; padding: 16px 0;
  border-bottom: 1px solid rgba(255,255,255,.14);
}
.lp-feat-icon { color: var(--accent); display: flex; margin-top: 3px; }
.lp-feat-title { font-size: 15px; font-weight: 600; }
.lp-feat-desc { font-size: 13.5px; color: rgba(255,255,255,.62); margin-top: 2px; }
.lp-side-foot { font-size: 12.5px; color: rgba(255,255,255,.45); margin-top: 40px; }

.lp-main {
  flex: 1 1 48%; display: flex; flex-direction: column; align-items: center; justify-content: center;
  padding: 48px 32px; background: #fff;
}
.lp-card { width: 100%; max-width: 380px; }
.lp-mobile-brand { display: none; }
.lp-card h2 { font-size: 28px; font-weight: 700; margin: 0 0 6px; letter-spacing: -.2px; }
.lp-lead { font-size: 14.5px; color: var(--muted); margin: 0 0 32px; }

.lp-label { display: block; font-size: 13.5px; font-weight: 600; color: #334155; margin-bottom: 8px; }
.lp-field { position: relative; margin-bottom: 20px; }
.lp-field input {
  width: 100%; height: 48px; padding: 0 44px 0 42px; box-sizing: border-box;
  border: 1px solid #cbd5e1; border-radius: 10px; background: #fff;
  font-family: inherit; font-size: 15px; color: var(--ink); outline: none;
  transition: border-color .15s, box-shadow .15s;
}
.lp-field input::placeholder { color: #a0aec0; }
.lp-field input:hover { border-color: #94a3b8; }
.lp-field input:focus { border-color: var(--brand-2); box-shadow: 0 0 0 3px rgba(29,79,134,.14); }
.lp-field-icon {
  position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; display: flex; pointer-events: none;
}
.lp-field:focus-within .lp-field-icon { color: var(--brand-2); }
.lp-eye {
  position: absolute; right: 6px; top: 50%; transform: translateY(-50%);
  width: 36px; height: 36px; border: none; background: none; border-radius: 8px;
  color: #94a3b8; cursor: pointer; display: flex; align-items: center; justify-content: center;
}
.lp-eye:hover { color: #475569; background: #f1f5f9; }

.lp-submit {
  width: 100%; height: 48px; margin-top: 8px; border: none; border-radius: 10px;
  background: var(--brand); color: #fff; font-family: inherit; font-size: 15.5px; font-weight: 600;
  display: flex; align-items: center; justify-content: center; gap: 10px; cursor: pointer;
  transition: background .15s;
}
.lp-submit:hover:not(:disabled) { background: var(--brand-2); }
.lp-submit:focus-visible { outline: 3px solid rgba(29,79,134,.35); outline-offset: 2px; }
.lp-submit:disabled { background: #7b93ad; cursor: not-allowed; }
.lp-spinner {
  width: 16px; height: 16px; border: 2px solid rgba(255,255,255,.4); border-top-color: #fff;
  border-radius: 50%; animation: lp-spin .7s linear infinite;
}
@keyframes lp-spin { to { transform: rotate(360deg); } }

.lp-student-entry {
  display: flex; align-items: center; gap: 14px; padding: 16px; margin-top: 22px;
  border: 1px solid #bfdbfe; border-radius: 12px; background: #eff6ff;
  color: #174e83; text-decoration: none; transition: background .15s, border-color .15s;
}
.lp-student-entry:hover { background: #dbeafe; border-color: #60a5fa; text-decoration: none; }
.lp-student-entry:focus-visible { outline: 3px solid #60a5fa; outline-offset: 3px; }
.lp-student-icon { display: flex; align-items: center; justify-content: center; width: 44px; height: 44px; flex-shrink: 0; background: #dbeafe; border-radius: 10px; }
.lp-student-copy { display: flex; flex: 1; flex-direction: column; gap: 3px; }
.lp-student-copy strong { font-size: 16px; }
.lp-student-copy > span { font-size: 13px; line-height: 1.5; }
.lp-student-entry > svg { flex-shrink: 0; }
.lp-help {
  font-size: 13px; color: var(--muted); margin: 28px 0 0; padding-top: 20px; border-top: 1px solid var(--line); line-height: 1.6;
}
.lp-mobile-foot { display: none; }

@media (max-width: 900px) {
  .lp-side { padding: 40px; }
  .lp-hero h1 { font-size: 32px; }
}
@media (max-width: 768px) {
  .lp-root { flex-direction: column; }
  .lp-side { display: none; }
  .lp-main { justify-content: flex-start; padding: 40px 24px 32px; min-height: 100vh; box-sizing: border-box; }
  .lp-mobile-brand {
    display: flex; align-items: center; gap: 12px; margin-bottom: 40px;
    font-size: 16px; font-weight: 700; color: var(--brand);
  }
  .lp-card h2 { font-size: 24px; }
  .lp-mobile-foot { display: block; margin-top: auto; padding-top: 32px; font-size: 12px; color: #94a3b8; }
}
`;
